import { useState, useEffect } from 'react';
import { authFetch } from '@/utils/authFetch';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Building, Mail, Phone, MapPin, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    company_name: '',
    contact_person: '',
    email: '',
    phone: '',
    address: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [clientsRes, userRes] = await Promise.all([
        authFetch(`${BACKEND_URL}/api/clients`),
        authFetch(`${BACKEND_URL}/api/auth/me`)
      ]);

      if (clientsRes.ok) setClients(await clientsRes.json());
      if (userRes.ok) setCurrentUser(await userRes.json());
    } catch (error) {
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await authFetch(`${BACKEND_URL}/api/clients`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('Client added successfully');
        setDialogOpen(false);
        setFormData({ company_name: '', contact_person: '', email: '', phone: '', address: '' });
        fetchData();
      } else {
        toast.error('Failed to add client');
      }
    } catch (error) {
      toast.error('Failed to add client');
    }
  };

  const handleDelete = async (clientId) => {
    if (!window.confirm('Delete this client?')) return;
    try {
      const response = await authFetch(`${BACKEND_URL}/api/clients/${clientId}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        toast.success('Client deleted');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to delete client');
    }
  };

  if (loading) return <Layout><div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div></div></Layout>;

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Client Management</h1>
            <p className="text-slate-600 mt-1">Manage your client relationships</p>
          </div>
          {currentUser?.role === 'admin' && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-indigo-600 hover:bg-indigo-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Client
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Add New Client</DialogTitle></DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div><Label>Company Name *</Label><Input value={formData.company_name} onChange={(e) => setFormData({...formData, company_name: e.target.value})} required /></div>
                  <div><Label>Contact Person *</Label><Input value={formData.contact_person} onChange={(e) => setFormData({...formData, contact_person: e.target.value})} required /></div>
                  <div><Label>Email *</Label><Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} required /></div>
                  <div><Label>Phone</Label><Input value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} /></div>
                  <div><Label>Address</Label><Textarea value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} rows={2} /></div>
                  <div className="flex justify-end space-x-2">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button type="submit">Add Client</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {clients.map((client) => (
            <Card key={client.client_id} className="card-hover">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="bg-indigo-100 p-2 rounded-md"><Building className="w-6 h-6 text-indigo-600" /></div>
                    <div><CardTitle className="text-base">{client.company_name}</CardTitle><p className="text-sm text-slate-500">{client.contact_person}</p></div>
                  </div>
                  {currentUser?.role === 'admin' && (
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(client.client_id)} className="text-red-600"><Trash2 className="w-4 h-4" /></Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex items-center text-sm text-slate-600"><Mail className="w-4 h-4 mr-2 text-slate-400" />{client.email}</div>
                {client.phone && <div className="flex items-center text-sm text-slate-600"><Phone className="w-4 h-4 mr-2 text-slate-400" />{client.phone}</div>}
                {client.address && <div className="flex items-start text-sm text-slate-600"><MapPin className="w-4 h-4 mr-2 mt-0.5 text-slate-400" /><span className="flex-1">{client.address}</span></div>}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </Layout>
  );
}